import { useFormik } from "formik";
import * as Yup from "yup";
import { useNavigate } from "react-router";
import { FaArrowLeft } from "react-icons/fa";
import { IMAGE_URL } from "../../config.js";
import { useRoForm } from "./RoContext.js";
import { useFileUpload } from "../../Common/PreSignUrl.jsx";
import { useState } from "react";

const steps = [
    { label: "Personal Info", link: "/personalinformationmanagerhub" },
    { label: "Residency Address", link: "/residencyaddmanager" },
    { label: "KYC", link: "/kycmanagerhub" },
    { label: "Bank Details", link: "/bankaccountdetailsmanagerhub" },
    { label: "Agreement", link: "/aggrementdetailsmanager" },
];

const BankAccountDetailsManagerHUB = ({ currentStep = 4 }) => {
    const { formData, updateFormData, completedSteps, setCompletedSteps } = useRoForm();
    const { handleFileUpload } = useFileUpload();
    const navigate = useNavigate();
    const [isLoading, setIsLoading] = useState(false);

    const blankForm = {
        accountNumber: "",
        accountHolder: "",
        ifsc: "",
        bankName: "",
        branchName: "",
        city: "",
        state: "",
        accountType: "",
        cancelledCheque: "",
    }
    const initialValues = { ...blankForm, ...formData };

    const formik = useFormik({
        initialValues: initialValues,
        validationSchema: Yup.object({
            accountNumber: Yup.string()
                .matches(/^\d{9,18}$/, "Enter a valid account number")
                .required("Account number is required"),
            accountHolder: Yup.string().required("Account holder name is required"),
            ifsc: Yup.string()
                .matches(/^[A-Z]{4}0[A-Z0-9]{6}$/, "Enter a valid IFSC code")
                .required("IFSC code is required"),
            bankName: Yup.string().required("Bank name is required"),
            branchName: Yup.string().required("Branch name is required"),
            city: Yup.string().required("City is required"),
            state: Yup.string().required("State is required"),
            accountType: Yup.string()
                .oneOf(["saving", "current"], "Invalid type")
                .required("Select account type"),
            cancelledCheque: Yup.mixed().required("Upload your cancelled cheque"),
        }),
        onSubmit: (values) => {
            if (!completedSteps.includes('/bankaccountdetailsmanagerhub')) {
                setCompletedSteps([...completedSteps, '/bankaccountdetailsmanagerhub']);
            }
            updateFormData(values, '/bankaccountdetailsmanagerhub'); // e.g., '/kycverification'


            // navigate to next step in wizard (example)
            navigate("/aggrementdetailsmanager");
            // Replace navigate with your API call to save data
            // console.log("Form submitted:", values);
            // console.log("Form Submitted:", values);
        },
    });

    const handleStepClick = (link) => navigate(link);
    const handleFileChange = (event, keyPrefix, folderName) => {
        if ((!event?.target?.files || !event?.currentTarget?.files) || event?.target?.files?.length === 0) {
            setIsLoading(false);
            // console.log("===");
        }
        handleFileUpload({
            event,
            keyPrefix,
            setStateCallback: (url) => {
                const newUrl = url.split('/').slice(-2).join('/');
                // console.log('✌️newUrl --->', newUrl);
                if (keyPrefix === "cancelledCheque") {
                    formik.setFieldValue("cancelledCheque", newUrl);
                }

                setIsLoading(false);
            },
            setIsLoading,
            folderName
        });
    }


    return (
        <div className="px-4 py-6 bg-white rounded-lg mb-10 grid grid-cols-1">
            {/* Back Button */}
            <button
                onClick={() => navigate(-1)}
                className="flex items-center gap-2 text-sm font-medium text-slate-700 hover:text-slate-900 mb-4"
            >
                <FaArrowLeft /> Back
            </button>

            {/* Header */}
            <h1 className="font-bold text-gray-800 mb-7">
                Step {currentStep}: Bank Account Details
            </h1>

            {/* Stepper */}
            <div className="flex justify-between pb-6 w-full overflow-x-auto whitespace-nowrap cursor-pointer items-center relative mb-4 scrollbar-hide">
                {steps.map((step, index) => (
                    <div
                        key={index}
                        className="flex-1 text-white flex flex-col items-center flex-shrink-0 min-w-[140px] relative"
                    >
                        {index !== 0 && (
                            <div className="absolute top-5 left-[-50%] w-[100%] h-0.5 bg-gray-300 z-0" />
                        )}
                        <div className="z-10" onClick={() => handleStepClick(step.link)}>
                            <div
                                className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold ${index + 1 === currentStep
                                    ? "bg-slate-900 text-white shadow-md"
                                    : "bg-white text-gray-500 border border-gray-300"
                                    }`}
                            >
                                {index + 1}
                            </div>
                        </div>
                        <span className="mt-2 text-sm text-center text-gray-700">
                            {step.label}
                        </span>
                    </div>
                ))}
            </div>

            {/* Form */}
            <div className="bg-white md:border md:p-6 rounded-lg md:shadow-sm">
                <form onSubmit={formik.handleSubmit} className="space-y-6">
                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                        {/* Account Number */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Account Number <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="accountNumber"
                                placeholder="Enter account number"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.accountNumber}
                                className={`w-full rounded-md px-4 py-2 text-sm focus:outline-none bg-blue-50 focus:ring-2 ${formik.touched.accountNumber && formik.errors.accountNumber
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.accountNumber && formik.errors.accountNumber && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.accountNumber}
                                </p>
                            )}
                        </div>

                        {/* Account Holder */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Account Holder Name <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="accountHolder"
                                placeholder="Enter account holder name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.accountHolder}
                                className={`w-full rounded-md px-4 py-2 text-sm focus:outline-none bg-blue-50 focus:ring-2 ${formik.touched.accountHolder && formik.errors.accountHolder
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.accountHolder && formik.errors.accountHolder && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.accountHolder}
                                </p>
                            )}
                        </div>

                        {/* IFSC */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                IFSC Code <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="ifsc"
                                placeholder="Enter IFSC code"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.ifsc}
                                className={`w-full rounded-md px-4 py-2 text-sm focus:outline-none bg-blue-50 focus:ring-2 ${formik.touched.ifsc && formik.errors.ifsc
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.ifsc && formik.errors.ifsc && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.ifsc}
                                </p>
                            )}
                        </div>

                        {/* Bank Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Bank Name <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="bankName"
                                placeholder="Enter bank name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.bankName}
                                className={`w-full rounded-md px-4 py-2 text-sm focus:outline-none bg-blue-50 focus:ring-2 ${formik.touched.bankName && formik.errors.bankName
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.bankName && formik.errors.bankName && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.bankName}
                                </p>
                            )}
                        </div>

                        {/* Branch Name */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Branch Name <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="branchName"
                                placeholder="Enter branch name"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.branchName}
                                className={`w-full rounded-md px-4 py-2 text-sm focus:outline-none bg-blue-50 focus:ring-2 ${formik.touched.branchName && formik.errors.branchName
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.branchName && formik.errors.branchName && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.branchName}
                                </p>
                            )}
                        </div>

                        {/* City */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                City <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="city"
                                placeholder="Enter city"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.city}
                                className={`w-full rounded-md px-4 py-2 text-sm focus:outline-none bg-blue-50 focus:ring-2 ${formik.touched.city && formik.errors.city
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.city && formik.errors.city && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.city}
                                </p>
                            )}
                        </div>

                        {/* State */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                State <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="text"
                                name="state"
                                placeholder="Enter state"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.state}
                                className={`w-full rounded-md px-4 py-2 text-sm focus:outline-none bg-blue-50 focus:ring-2 ${formik.touched.state && formik.errors.state
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.touched.state && formik.errors.state && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.state}
                                </p>
                            )}
                        </div>

                        {/* Account Type */}
                        <div>
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Account Type <span className="text-red-600">*</span>
                            </label>
                            <select
                                name="accountType"
                                onChange={formik.handleChange}
                                onBlur={formik.handleBlur}
                                value={formik.values.accountType}
                                className={`w-full rounded-md px-4 py-2 text-sm focus:outline-none bg-blue-50 focus:ring-2 ${formik.touched.accountType && formik.errors.accountType
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            >
                                <option value="">Select account type</option>
                                <option value="saving">Saving</option>
                                <option value="current">Current</option>
                            </select>
                            {formik.touched.accountType && formik.errors.accountType && (
                                <p className="text-red-500 text-xs mt-1">
                                    {formik.errors.accountType}
                                </p>
                            )}
                        </div>

                        {/* Cancelled Cheque Upload */}
                        <div className="col-span-1 md:col-span-3">
                            <label className="block text-sm font-medium text-gray-700 mb-1">
                                Cancelled Cheque <span className="text-red-600">*</span>
                            </label>
                            <input
                                type="file"
                                name="cancelledCheque"
                                accept="image/*"
                                onChange={(e) => handleFileChange(e, "cancelledCheque", "franchiseDetails")}
                                onBlur={formik.handleBlur}
                                className={`w-full rounded-md px-3 py-2 text-sm bg-blue-50 focus:outline-none focus:ring-2 ${formik.touched.cancelledCheque && formik.errors.cancelledCheque
                                    ? "border border-red-500 focus:ring-red-500"
                                    : "border border-gray-300 focus:ring-slate-500"
                                    }`}
                            />
                            {formik.values.cancelledCheque && (
                                <div className="relative w-full h-40 border rounded-lg overflow-hidden shadow-sm mt-2">
                                    <img
                                        src={
                                            typeof formik.values.cancelledCheque === "string"
                                                ? `${IMAGE_URL}/${formik.values.cancelledCheque}`
                                                : URL.createObjectURL(formik.values.cancelledCheque)
                                        }
                                        alt="Cancelled Cheque Preview"
                                        className="object-cover w-full h-full"
                                    />
                                    <a
                                        href={
                                            typeof formik.values.cancelledCheque === "string"
                                                ? `${IMAGE_URL}/${formik.values.cancelledCheque}`
                                                : URL.createObjectURL(formik.values.cancelledCheque)
                                        }
                                        download="cancelled-cheque.jpg"
                                        className="absolute bottom-2 right-2 bg-slate-700 text-white text-xs font-medium px-2 py-1 rounded hover:bg-slate-800"
                                    >
                                        Download
                                    </a>
                                </div>
                            )}
                            {formik.touched.cancelledCheque &&
                                formik.errors.cancelledCheque && (
                                    <p className="text-red-500 text-xs mt-1">
                                        {formik.errors.cancelledCheque}
                                    </p>
                                )}
                        </div>
                    </div>

                    {/* Submit */}
                    <div className="flex justify-end">
                        <button
                            type="submit"
                            className="bg-slate-900 hover:bg-slate-800 text-white px-6 py-2 rounded-lg font-semibold shadow-md transition"
                        >
                            Next
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
};

export default BankAccountDetailsManagerHUB;

function Page() {
    return <BankAccountDetailsManagerHUB currentStep={4} />;
}
